import { inject, Injectable } from '@angular/core';
import { Action, State, StateContext } from '@ngxs/store';
import { catchError, EMPTY, tap } from 'rxjs';
import { LoadableStatus, Logger, SnackbarService, SnackbarType } from '@aiwyn/shared';
import { CxpTasksStateModel, DEFAULT_CXP_TASKS_STATE } from './cxp-tasks.model';
import { ResetState } from '../../../shared/state/meta.actions';
import {
  LoadCxpStaff,
  LoadCxpStaffSuccess,
  LoadCxpTaskCount,
  LoadCxpTaskCountSuccess,
  LoadCxpTasks,
  LoadCxpTasksFailure,
  LoadCxpTasksSuccess,
  RefreshCxpTasks,
  SetCxpActiveRouteParams,
} from './cxp-tasks.actions';
import { CxpTasksService } from '../services/cxp-tasks.service';

export const CXP_TASKS_STATE_NAME = 'cxpTasks';

@State<CxpTasksStateModel>({
  name: CXP_TASKS_STATE_NAME,
  defaults: DEFAULT_CXP_TASKS_STATE,
})
@Injectable()
export class CxpTasksState {
  private readonly cxpTasksService = inject(CxpTasksService);
  private readonly logger = inject(Logger);
  private readonly snackbarService = inject(SnackbarService);

  @Action(ResetState)
  resetState({ setState }: StateContext<CxpTasksStateModel>): void {
    setState(DEFAULT_CXP_TASKS_STATE);
  }

  @Action(SetCxpActiveRouteParams)
  setActiveRouteParams(ctx: StateContext<CxpTasksStateModel>, { requestId }: SetCxpActiveRouteParams) {
    ctx.patchState({ selectedRequestId: requestId });
  }

  @Action(RefreshCxpTasks)
  refreshTasks(ctx: StateContext<CxpTasksStateModel>) {
    if (ctx.getState().status === LoadableStatus.LOADING) return;
    // Reset status so `_loadTasksIfNeeded$` re-fires. Clear staff so stale
    // entries don't persist; keep the existing rows until the reload replaces
    // them (avoids an empty flash).
    ctx.patchState({ status: LoadableStatus.DEFAULT, staff: [] });
  }

  @Action(LoadCxpTasks)
  loadTasks(ctx: StateContext<CxpTasksStateModel>, { silent }: LoadCxpTasks) {
    ctx.patchState({ status: LoadableStatus.LOADING });

    return this.cxpTasksService.fetchAll().pipe(
      tap((tasks) => ctx.dispatch(new LoadCxpTasksSuccess(tasks))),
      catchError((error) => {
        if (silent) {
          ctx.patchState({ status: LoadableStatus.DEFAULT });
          return EMPTY;
        }
        ctx.dispatch(new LoadCxpTasksFailure(error));
        return EMPTY;
      }),
    );
  }

  @Action(LoadCxpTasksSuccess)
  loadTasksSuccess(ctx: StateContext<CxpTasksStateModel>, { tasks }: LoadCxpTasksSuccess) {
    ctx.patchState({ status: LoadableStatus.LOADED, tasks });
    ctx.dispatch(new LoadCxpStaff());
  }

  @Action(LoadCxpTasksFailure)
  loadTasksFailure(ctx: StateContext<CxpTasksStateModel>, { error }: LoadCxpTasksFailure) {
    this.logger.error('[CxpTasksState] loadTasks failed:', error);
    this.snackbarService.open('Failed to load tasks. Please try again later.', SnackbarType.ERROR);
    ctx.patchState({ status: LoadableStatus.ERROR });
  }

  @Action(LoadCxpStaff)
  loadStaff(ctx: StateContext<CxpTasksStateModel>) {
    const staffIds = [...new Set(ctx.getState().tasks.flatMap((task) => task.staffIds))];
    if (staffIds.length === 0) return;

    return this.cxpTasksService.fetchStaff(staffIds).pipe(
      tap((staff) => ctx.dispatch(new LoadCxpStaffSuccess(staff))),
      catchError((error) => {
        this.logger.error('[CxpTasksState] loadStaff failed:', error);
        return EMPTY;
      }),
    );
  }

  @Action(LoadCxpStaffSuccess)
  loadStaffSuccess(ctx: StateContext<CxpTasksStateModel>, { staff }: LoadCxpStaffSuccess) {
    ctx.patchState({ staff });
  }

  @Action(LoadCxpTaskCount)
  loadTaskCount(ctx: StateContext<CxpTasksStateModel>) {
    return this.cxpTasksService.fetchOpenTaskCount().pipe(
      tap((openCount) => ctx.dispatch(new LoadCxpTaskCountSuccess(openCount))),
      catchError((error) => {
        this.logger.warn('[CxpTasksState] loadTaskCount failed:', error);
        return EMPTY;
      }),
    );
  }

  @Action(LoadCxpTaskCountSuccess)
  loadTaskCountSuccess(ctx: StateContext<CxpTasksStateModel>, { openCount }: LoadCxpTaskCountSuccess) {
    ctx.patchState({ badgeOpenCount: openCount });
  }
}
